/**
 * Debug page for visualizing planar graph half edges with indices
 * @module
 */
import { createFileRoute } from "@tanstack/react-router";
import { generateBuilding } from "building-generation";
import { generationStagesForPreciseFloorArea } from "building-generation";
import type { Building } from "building-types/building/buildingTypes";
import { uploadFile } from "common/utils/uploadFile";
import { FILE_EXT, docParse } from "doc/docMarshal";
import { aabb2ByPolyLine2s } from "geom/aabb2";
import { planarGraphHalfEdgeStripToPolyLine2 } from "geom/planarGraph";
import {
  renderPlanarGraphHalfEdges,
  renderPlanarGraphVertices,
  renderPlanarGraphFaces,
  renderPlanarGraphFaceLabels,
} from "geom/planarGraphRender";
import type { PolyLine2 } from "geom/polyLine2Types";
import { useState, useCallback } from "react";
import withAuthentication from "../../components/withAuthentication";
import Canvas from "./polyclip/-Canvas";

function PlanarGraphDebugPage() {
  const [building, setBuilding] = useState<Building | null>(null);
  const [selectedLevelId, setSelectedLevelId] = useState<string | null>(null);
  const [showFaces, setShowFaces] = useState<boolean>(true);
  const [showHalfEdges, setShowHalfEdges] = useState<boolean>(true);
  const [showVertices, setShowVertices] = useState<boolean>(false);
  const [showFaceLabels, setShowFaceLabels] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadHomeFile = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const file = await uploadFile("application/json," + FILE_EXT);
      const doc = docParse(file.body);

      const bldg = await generateBuilding({
        doc,
        optionChoices: {},
        stages: generationStagesForPreciseFloorArea,
        printWarningsToConsole: false,
        throwOnError: false,
        showPerformance: false,
      });

      setBuilding(bldg);

      // Set first level as selected by default
      const levelIds = Object.keys(bldg.planarGraphs);
      if (levelIds.length > 0) {
        setSelectedLevelId(levelIds[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load .home file");
    } finally {
      setIsLoading(false);
    }
  };

  const renderMainVisualization = useCallback(
    (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => {
      if (!building || !selectedLevelId) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "black";
        ctx.font = "20px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("Load a .home file to visualize planar graphs", canvas.width / 2, canvas.height / 2);
        return;
      }

      const planarGraphData = building.planarGraphs[selectedLevelId];
      if (!planarGraphData) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "black";
        ctx.font = "20px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(`No planar graph found for level: ${selectedLevelId}`, canvas.width / 2, canvas.height / 2);
        return;
      }

      const planarGraph = planarGraphData.planarGraph;

      // Calculate bounding box
      const allPolylines: PolyLine2[] = [];
      for (let i = 0; i < planarGraph.faceHalfEdges.length; i++) {
        const polyline = planarGraphHalfEdgeStripToPolyLine2(planarGraph, planarGraph.faceHalfEdges[i]);
        allPolylines.push(polyline);
      }

      const aabb = aabb2ByPolyLine2s(allPolylines);
      if (!aabb) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();

      // Fill white background
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Calculate scale and transform
      const PADDING = 20;
      const minX = aabb.minX - PADDING;
      const minY = aabb.minY - PADDING;
      const maxX = aabb.maxX + PADDING;
      const maxY = aabb.maxY + PADDING;

      const width = maxX - minX;
      const height = maxY - minY;

      const scaleFactor = Math.min(canvas.width / width, canvas.height / height);
      const ratio = window.devicePixelRatio;
      const ss = scaleFactor / ratio;

      ctx.translate(-ss * minX, canvas.height / ratio + ss * minY);
      ctx.scale(ss, -ss);

      // Render faces
      if (showFaces) {
        renderPlanarGraphFaces(ctx, planarGraph, true, true);
      }

      // Render face labels
      if (showFaceLabels) {
        renderPlanarGraphFaceLabels(ctx, planarGraph);
      }

      // Render half edges with indices
      if (showHalfEdges) {
        renderPlanarGraphHalfEdges(ctx, planarGraph);
      }

      // Render vertices
      if (showVertices) {
        renderPlanarGraphVertices(ctx, planarGraph);
      }

      ctx.restore();
    },
    [building, selectedLevelId, showFaces, showHalfEdges, showVertices, showFaceLabels],
  );

  const levelIds = building ? Object.keys(building.planarGraphs) : [];

  return (
    <>
      <div style={{ padding: "20px" }}>
        <h1>Planar Graph Half Edge Debug Viewer</h1>

        <div style={{ marginBottom: "20px" }}>
          <button
            onClick={loadHomeFile}
            disabled={isLoading}
            style={{
              padding: "10px 20px",
              fontSize: "16px",
              backgroundColor: isLoading ? "#ccc" : "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
          >
            {isLoading ? "Loading..." : "Load .home File"}
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              marginBottom: "20px",
            }}
          >
            Error: {error}
          </div>
        )}

        {building && levelIds.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <label style={{ marginRight: "10px" }}>Select Level:</label>
            <select
              value={selectedLevelId || ""}
              onChange={(e) => setSelectedLevelId(e.target.value)}
              style={{
                padding: "5px 10px",
                fontSize: "14px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            >
              {levelIds.map((levelId) => (
                <option key={levelId} value={levelId}>
                  {levelId}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ marginBottom: "20px" }}>
          <label style={{ marginRight: "20px" }}>
            <input
              type="checkbox"
              checked={showFaces}
              onChange={(e) => setShowFaces(e.target.checked)}
              style={{ marginRight: "5px" }}
            />
            Show Faces
          </label>
          <label style={{ marginRight: "20px" }}>
            <input
              type="checkbox"
              checked={showHalfEdges}
              onChange={(e) => setShowHalfEdges(e.target.checked)}
              style={{ marginRight: "5px" }}
            />
            Show Half Edges (with indices)
          </label>
          <label style={{ marginRight: "20px" }}>
            <input
              type="checkbox"
              checked={showVertices}
              onChange={(e) => setShowVertices(e.target.checked)}
              style={{ marginRight: "5px" }}
            />
            Show Vertices
          </label>
          <label>
            <input
              type="checkbox"
              checked={showFaceLabels}
              onChange={(e) => setShowFaceLabels(e.target.checked)}
              style={{ marginRight: "5px" }}
            />
            Show Face Labels
          </label>
        </div>

        {building && selectedLevelId && (
          <div style={{ marginBottom: "10px", fontSize: "14px", color: "#666" }}>
            Planar Graph Info:
            <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
              <li>Number of faces: {building.planarGraphs[selectedLevelId].planarGraph.faceHalfEdges.length}</li>
              <li>Number of half edges: {building.planarGraphs[selectedLevelId].planarGraph.halfEdges.length / 2}</li>
              <li>
                Number of vertices: {building.planarGraphs[selectedLevelId].planarGraph.vertexPositions.length / 2}
              </li>
            </ul>
          </div>
        )}

        <Canvas canvasWidth={1200} canvasHeight={800} render={renderMainVisualization} />
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/planar-graph")({
  component: withAuthentication(PlanarGraphDebugPage, { superAdmin: true }),
});
